import React, { useState, useEffect } from 'react';
import {
  X,
  Folder,
  Tag,
  Trash2,
  AlertTriangle,
  Check,
  Plus,
} from 'lucide-react';

// Move Identity to Category Modal
export function MoveIdentityCategoryModal({
  open,
  identity,
  categories = [],
  onClose,
  onSave,
}) {
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    if (identity) {
      setSelectedCategory(identity.category || categories[0]?.name || 'Personal');
    }
  }, [identity, categories]);

  if (!open || !identity) return null;

  const handleSave = (e) => {
    e.preventDefault();
    onSave(identity, selectedCategory);
    onClose();
  };

  const allCategories = [
    { id: 'cat-personal', name: 'Personal', icon: '👤' },
    { id: 'cat-family', name: 'Family', icon: '👨‍👩‍👧‍👦' },
    { id: 'cat-work', name: 'Work', icon: '💼' },
    { id: 'cat-legal', name: 'Legal', icon: '⚖️' },
    { id: 'cat-medical', name: 'Medical', icon: '🩺' },
    ...categories.filter(
      (c) => !['Personal', 'Family', 'Work', 'Legal', 'Medical'].includes(c.name)
    ),
  ];

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer identity-drawer ios-identity-drawer identity-category-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label="Move to Category"
      >
        <div className="card-drawer-header modal-header">
          <div className="identity-activity-title-group">
            <div
              className="modal-icon-badge"
              style={{ background: 'rgba(99, 102, 241, 0.15)', color: '#6366f1' }}
            >
              <Folder size={18} />
            </div>
            <div>
              <h3 className="modal-title">Move to Category</h3>
              <p className="modal-subtitle">
                Choose a destination category for "{identity.title || 'Untitled Identity'}".
              </p>
            </div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSave} className="card-drawer-form-wrapper">
          <div className="card-drawer-body modal-body">
            <div className="category-select-grid">
              {allCategories.map((cat) => (
                <label
                  key={cat.id || cat.name}
                  className={`category-select-option ${
                    selectedCategory === cat.name ? 'selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.name}
                    checked={selectedCategory === cat.name}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    style={{ display: 'none' }}
                  />
                  <span className="category-option-icon">{cat.icon || '📁'}</span>
                  <span className="category-option-name">{cat.name}</span>
                  {selectedCategory === cat.name && (
                    <Check size={15} className="category-option-check" />
                  )}
                </label>
              ))}
            </div>
          </div>

          <div className="card-drawer-footer modal-footer identity-editor-footer">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm">
              Move Identity
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

// Manage Identity Tags Modal
export function ManageIdentityTagsModal({
  open,
  identity,
  onClose,
  onSave,
}) {
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (identity) {
      setTags(Array.isArray(identity.tags) ? [...identity.tags] : []);
      setTagInput('');
    }
  }, [identity]);

  if (!open || !identity) return null;

  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = tagInput.trim().toLowerCase().replace(/^#/, '');
      if (val && !tags.includes(val)) {
        setTags([...tags, val]);
      }
      setTagInput('');
    }
  };

  const handleAddTagClick = () => {
    const val = tagInput.trim().toLowerCase().replace(/^#/, '');
    if (val && !tags.includes(val)) {
      setTags([...tags, val]);
    }
    setTagInput('');
  };

  const handleRemoveTag = (tag) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleSave = (e) => {
    e.preventDefault();
    onSave(identity, tags);
    onClose();
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer identity-drawer ios-identity-drawer identity-tags-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label="Manage Tags"
      >
        <div className="card-drawer-header modal-header">
          <div className="identity-activity-title-group">
            <div
              className="modal-icon-badge"
              style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}
            >
              <Tag size={18} />
            </div>
            <div>
              <h3 className="modal-title">Manage Tags</h3>
              <p className="modal-subtitle">
                Label and organize "{identity.title || 'Untitled Identity'}"
              </p>
            </div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSave} className="card-drawer-form-wrapper">
          <div className="card-drawer-body modal-body">
            <div className="form-group">
              <label className="form-label">Add New Tag</label>
              <div className="tag-input-row" style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. passport, renewal-2026, travel"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleAddTagClick}
                >
                  <Plus size={15} />
                  <span>Add</span>
                </button>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '16px' }}>
              <label className="form-label">Current Tags ({tags.length})</label>
              <div
                className="tags-chip-container"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  minHeight: '42px',
                  padding: '10px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                }}
              >
                {tags.length === 0 ? (
                  <span className="text-secondary text-sm">No tags added yet.</span>
                ) : (
                  tags.map((tag) => (
                    <span key={tag} className="tag-chip">
                      #{tag}
                      <button
                        type="button"
                        className="tag-remove-btn"
                        onClick={() => handleRemoveTag(tag)}
                      >
                        &times;
                      </button>
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="card-drawer-footer modal-footer identity-editor-footer">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm">
              Save Tags
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

// Permanent Delete Identity Modal
export function DeleteIdentityModal({
  open,
  identity,
  isPermanent = false,
  onClose,
  onConfirm,
}) {
  if (!open || !identity) return null;

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer identity-drawer ios-identity-drawer identity-delete-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label="Delete Identity"
      >
        <div className="card-drawer-header modal-header">
          <div className="identity-activity-title-group">
            <div
              className="modal-icon-badge"
              style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' }}
            >
              <AlertTriangle size={18} />
            </div>
            <div>
              <h3 className="modal-title">
                {isPermanent ? 'Permanently Delete Identity' : 'Move Identity to Trash'}
              </h3>
              <p className="modal-subtitle">
                {isPermanent
                  ? 'This action is irreversible. All encrypted documents will be destroyed.'
                  : 'You can restore this identity from the Trash anytime.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="card-drawer-body modal-body">
          <p style={{ margin: '10px 0', fontSize: '14px', lineHeight: 1.5 }}>
            Are you sure you want to {isPermanent ? 'permanently erase' : 'move'}{' '}
            <strong>"{identity.title || 'Untitled Identity'}"</strong>
            {isPermanent
              ? ' from your zero-knowledge vault? The cryptographic key and attached document scans will be purged permanently.'
              : ' to the trash?'}
          </p>
        </div>

        <div className="card-drawer-footer modal-footer identity-editor-footer">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => {
              onConfirm(identity);
              onClose();
            }}
          >
            <Trash2 size={14} />
            <span>{isPermanent ? 'Erase Forever' : 'Move to Trash'}</span>
          </button>
        </div>
      </aside>
    </div>
  );
}
